// Import Axios to issue http requests
import axios from 'axios';
import { SearchCriteria, SearchModality, Species } from '../Types';
import { updateJob } from '../storage/MySearchesStore';

// Define the Constant for where the API is hosted
const API_BASE_ADDRESS = 'https://api.sirna.ai';

// Create and configure our axios instance. Our instance will be connected to our API.
const instance = axios.create({
  baseURL: API_BASE_ADDRESS, //TODO: load from environment variable.
});

// Since we are using Typescript, we can define types for all API-Interaction.
// These defined types specify which members we expect to be on API Response objects.
type APIResponse = {}; // Base-type for API-Response.

export interface APIErrorResponse extends APIResponse {
  error : boolean,
  errorMessage : string,
}

export function is_APIErrorResponse(object: any): object is APIErrorResponse {
  return 'error' in object && "errorMessage" in object; 
}

/**
 * Represent the state that job can be in.
 */
export enum JobStatus {
  NON_EXISTENT = "NON_EXISTENT", // For Jobs that never existed. 

  QUEUED = "QUEUED", // This job is queued to be run.
  RUNNING = "RUNNING", // This is the Active job that the algorithm is computing a solution for.

  FINISHED_WITH_RESULTS = "FINISHED_WITH_RESULTS", // The job ran, and has finished with results.
  FINISHED_WITH_ERROR = "FINISHED_WITH_ERROR", // The job ran, and has finished with errors.

  DELETED = "DELETED", // We will persist job results for 7 days, after that time, Jobs will be deleted.
}

export interface JobRecord extends APIResponse {
  uuid : string,
  status : JobStatus,
  positionInJobQueue : number,
  searchCriteria : SearchCriteria,
}

export function is_JobRecord(object: any): object is JobRecord {
  const hasUUID = 'uuid' in object;
  const hasJobStatus = 'status' in object && object.status in JobStatus; 
  return hasUUID && hasJobStatus;
}

export interface AccessionNumberRecord extends APIResponse {
  accessionNumber : string,
  species : string,
}

export function is_AccessionNumberRecord(object: any): object is AccessionNumberRecord {
  try {
    const hasAccessionNumber = 'accessionNumber' in object;
    const hasSpecies = 'species' in object; 
    return hasSpecies && hasAccessionNumber;
  } catch(err) {
    return false;
  }
}

export interface GeneNameRecord extends APIResponse {
  geneName : string,
  geneAlias : Array<string>,
  species : string,
  accessionRegions : Array<string>
}

export interface BackendMetadataRecord extends APIResponse {
  supportedSpecies : Array<Species>,
  numLoadedAccessions : number,
  numCompletedJobs : number,
  numQueuedJobs : number, 
  numRunningJobs : number,
  maxConcurrentJobs : number,
}

export function is_GeneNameRecord(object: any): object is GeneNameRecord {
  try {
    const hasAccessionNumber = 'geneName' in object;
    return hasAccessionNumber;
  } catch(err) {
    return false;
  }
}

// Define all of the HTTP methods that we could want our API wrapper to issue.
enum HTTP_METHOD {
  GET,
  POST,
  PUT,
  HEAD,
  DELETE,
  PATCH,
  OPTIONS,
  CONNECT,
  TRACE,
}

// Define all of the HTTP Endpoints that we will be interacting with.
const START_JOB = "/search"
const JOB_STATUS = "/search"
const VALIDATE = "/validate"
const _ACCESSION_NUMBER = "/accession"
const _GENE_NAME = "/gene"
const _METADATA_URL = "metadata";

// Define our API functions TODO: Take in search credentials
export function search (searchCriteria : SearchCriteria) : Promise<JobRecord | APIErrorResponse> {
  return new Promise<JobRecord | APIErrorResponse>((resolve, reject) => {

    // Construct our request body. This ensures the values we want to reference are populated.
    const requestBody : SearchCriteria = {
      "modality" : searchCriteria.modality ?? SearchModality.SEARCH_BY_ACCESSION_NUMBER,
      "species" : searchCriteria.species,
      "gene" : searchCriteria.gene ?? "",
      "accessionNumber" : searchCriteria.accessionNumber ?? "NM_00000000",
      "sequence" : searchCriteria.sequence ?? ""
    }

    console.log('requestBody', requestBody); 
    
    // Call the API
    apiCallWrapper(HTTP_METHOD.POST, START_JOB, -1, requestBody).then((data) => {
      // Convert the JSON returned to a JobRecord 
      let job : JobRecord = data as JobRecord;
      // Resolve this promise and return the job to the user.
      resolve(job);
    }).catch((error) => {
      reject(error as APIErrorResponse);
    })
  });
}

export function getJobStatus (jobID : string) : Promise<JobRecord | APIErrorResponse> {
  // Since this is a Get Request, we encode data in the URL of the request.
  // We need to append the ID of the job we are interested in querying the status of.

  // Build the request object to append to the request
  const requestObject = {
    uuid : jobID
  }
  // Build the requestURL
  let requestURL = appendGetMembersToURL(JOB_STATUS, requestObject);

  // Issue the apiCall
  return new Promise<JobRecord | APIErrorResponse>((resolve, reject) => {
    // Ensure user passes a jobID to this function
    if (!jobID) {
      return reject({
        error : true,
        errorMessage : "",
      } as APIErrorResponse)
    } else {
      // We have a JobID, so lets search for this job's information.
      apiCallWrapper(HTTP_METHOD.GET, requestURL).then((data) => {
        if (is_JobRecord(data)) {
          // Cast to JobRecord
          let jobRecord = (data as JobRecord);
          // Also cache this data in our jobs table
          updateJob(jobRecord);
          // Resolve the data
          resolve(jobRecord);
        } else {
          reject(data);
        }
      }).catch((error) => {
        reject(error as APIErrorResponse);
      })
    }
  });
}

export function getJobResults (jobID : string) : Promise<string | APIErrorResponse> {
  // Since this is a Get Request, we encode data in the URL of the request.
  // We need to append the ID of the job we are interested in querying the status of.

  // Build the request object to append to the request
  const requestObject = {
    uuid : jobID
  }

  // Build the requestURL
  let requestURL = appendGetMembersToURL("download", requestObject);

  // Issue the apiCall
  return new Promise<string | APIErrorResponse>((resolve, reject) => {
    // Check if jobID is valid
    if (!jobID) {
      // Build our error message
      const errorMessage : APIErrorResponse = {
        error : true,
        errorMessage: "Supplied value for\"jobID\" is invalid."
      }
      // Return our rejection
      return reject(errorMessage);
    }

    // API request.
    apiCallWrapper(HTTP_METHOD.GET, requestURL).then((data) => {
      resolve(data as string);
    }).catch((error) => {
      reject(error as APIErrorResponse);
    })
  });
}

export function checkAccessionNumber (accessionNumber : string) : Promise<AccessionNumberRecord | APIErrorResponse> {
  
  // Build the request object to append to the request
  const requestObject = {
    accession_number : accessionNumber
  }

  // Build the requestURL
  let requestURL = appendGetMembersToURL(VALIDATE + _ACCESSION_NUMBER, requestObject);
  
  // Issue the apiCall
  return new Promise<AccessionNumberRecord | APIErrorResponse>((resolve, reject) => {
    apiCallWrapper(HTTP_METHOD.GET, requestURL).then((data) => {
      if (is_AccessionNumberRecord(data)) {
        resolve(data as AccessionNumberRecord);
      } else {
        // Error
        reject("Response is not Accession Number");
      }
    }).catch((error) => {
      reject(error as APIErrorResponse);
    })
  });
}

export function checkGeneName (geneName : string, species : Species) : Promise<GeneNameRecord | APIErrorResponse> {
  // Build the request object to append to the request
  const requestObject = {
    gene_name : geneName,
    species : species
  }

  // Build the requestURL
  let requestURL = appendGetMembersToURL(VALIDATE + _GENE_NAME, requestObject);
  
  // Issue the apiCall
  return new Promise<GeneNameRecord | APIErrorResponse>((resolve, reject) => {
    apiCallWrapper(HTTP_METHOD.GET, requestURL).then((data) => {
      console.log(data)
      if (is_GeneNameRecord(data)) {
        resolve(data as GeneNameRecord);
      } else {
        // Error
        reject("Response is not Gene Name");
      }
    }).catch((error) => {
      reject(error as APIErrorResponse);
    })
  });
}

export function getBackendMetadata () : Promise<BackendMetadataRecord | APIErrorResponse> {
  // Issue the apiCall
  return new Promise<BackendMetadataRecord | APIErrorResponse>((resolve, reject) => {
    apiCallWrapper(HTTP_METHOD.GET, _METADATA_URL).then((data) => {
      resolve(data as BackendMetadataRecord);
    }).catch((error) => {
      reject(error as APIErrorResponse);
    })
  });
}

/**
 * 
 * @param http_method 
 * @param url 
 * @param timeout_ms 
 * @returns 
 */
function apiCallWrapper(http_method : HTTP_METHOD, url : string, timeout_ms : number = -1, body : SearchCriteria | null = null) : Promise<APIResponse>{
  return new Promise((resolve, reject) => {
    // The timeout variable is either a Timeout or null 
    let timeout : ReturnType<typeof setTimeout> | null = null
    // Since we are using AXIOS, we are able to abort the request through an abort controller.
    const controller : AbortController = new AbortController();
    // Sometimes we want to issue a request that times out after an amount of time. If we have a timeout parameter > 0, start a timeout. 
    if (timeout_ms > 0) {
      // Create a new timeout and assign the timerID of the new timeout to the variable "timeout".
      timeout = setTimeout(() => {
        // Abort our AXIOS request
        controller.abort();
      }, timeout_ms); // This timeout function will be executed after timeout_ms milliseconds. 
    }

    type AxiosMethod = 'get' | 'post' | 'put' | 'delete' | 'patch' | 'head' | 'options';

    const method = HTTP_METHOD[http_method].toLowerCase() as AxiosMethod;

    const axios_instance_config = {
      signal: controller.signal,
      params: {
        // optional query params
      },
    };

    let responsePromise: Promise<any>;

    // POST/PUT/PATCH methods require a data/body parameter
    if (['post', 'put', 'patch'].includes(method)) {
      responsePromise = (instance[method] as (url: string, data?: any, config?: any) => Promise<any>)(
        url,
        body ?? {}, // provide empty object if body is undefined
        axios_instance_config
      );
    } else {
      // GET/DELETE/HEAD/OPTIONS methods only take url + config
      responsePromise = (instance[method] as (url: string, config?: any) => Promise<any>)(
        url,
        axios_instance_config
      );
    }

    responsePromise
      .then((response) => resolve(response.data))
      .catch((error) => reject(error))
      .finally(() => clearTimeout(timeout!));
  });
}

/**
 * 
 * @param urlBase 
 * @param members 
 * @returns 
 */
function appendGetMembersToURL(urlBase : string, members : any) : string {
  // Define the member that will be returned from this function.
  // By default this is just the URL base. We will append objects to this.
  let out = urlBase;
  // If the user-passed members are valid.
  if (members) {
    // Since we have members, we need to add a "?" to the url
    out += '?';
    // Get all of the keys of the object.
    const keys = Object.keys(members);
    // Keep track of the index
    let index = 0;
    // Iterate through the keys of the members object.
    for (let key of keys) {
      // Add each key-value pair to the url
      out += key + '=' + members[key];
      // Increment our index
      index++;
      // If our index is less than the number of keys, add a ','.
      if (index < keys.length) {
        out += '&';
      }
    }
  }
  // Return the members of the object appended to the 
  return out;
}